import * as React from 'react';
// Import services.
import {
	AddressService,
} from '@app/services';
// Import form-input hooks.
import {
	useInputBuildingCoverageLimits,
	UseInputBuildingCoverageLimitsResults,
} from '@app/hooks/view/input';
// Import form hooks.
import {
	useFormBuildingInfo,
	UseFormBuildingInfoResults,
	useComparativeEntitySummary,
	UseComparativeEntitySummaryResults,
} from '@app/hooks/view';
// Import props.
import {
	ComparativeBuildingSummaryProps,
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
} from '@app/props';
// Import enumerations.
import {
	EntityInstanceType,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	AddressLocationWithAccuracy,
	BuildingLimitsViewSummary,
	BuildingResponseCommercialExisting,
	BuildingRiskDataCommercial,
} from '@app/models/api';
// Import interfaces.
import {
	IAddress,
} from '@app/interfaces';

interface UseComparativeBuildingSummaryParameters {
	props: ComparativeBuildingSummaryProps;
}

interface UseComparativeBuildingSummaryResults {
	showTheAddressComparison: boolean;
	showBuildingDetails: boolean;
	showBuildingLimits: boolean
	comparativeEntitySummary: UseComparativeEntitySummaryResults;
	oldBuildingCoverageLimitsInput: UseInputBuildingCoverageLimitsResults;
	oldFormBuildingInfo: UseFormBuildingInfoResults;
	newBuildingCoverageLimitsInput: UseInputBuildingCoverageLimitsResults;
	newFormBuildingInfo: UseFormBuildingInfoResults;
	applyAddressComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyBuildingDetailsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyBuildingLimitsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyAddressComparativeDataRowProps: () => ComparativeDataRowProps<IAddress>;
	applyBuildingDetailsComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyBuildingLimitsComparativeDataRowProps: () => ComparativeDataRowProps[];
}

const useComparativeBuildingSummary = (params: UseComparativeBuildingSummaryParameters): UseComparativeBuildingSummaryResults => {

	// #region exported methods

	// #region building address

	/**
	 * Returns address comparative data header props.
	 */ 
	const applyAddressComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForAddressHeader(),
			isSticky: false,
			title: 'Address',
			titleFontSize: 'xsm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	}

	/**
	 * Returns address comparative data row props. 
	 **/
	const applyAddressComparativeDataRowProps = (): ComparativeDataRowProps<IAddress> => {
		const oldAddress: IAddress | undefined = oldFormBuildingInfo.subformBuildingInfoAddress.buildingAddressSummaryInput.value.address
			?? newFormBuildingInfo.subformBuildingInfoAddress.buildingAddressSummaryInput.value.address
			?? undefined;
		const newAddress: IAddress | undefined = newFormBuildingInfo.subformBuildingInfoAddress.buildingAddressSummaryInput.value.address
			?? undefined;

		return {
			oldValue: oldAddress,
			newValue: newAddress,
			hideNewValueIfSame: true,
			hideDivider: true,
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	// #endregion building address

	// #region building details

	/**
	 * Returns building details comparative data header props.
	 */
	const applyBuildingDetailsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForBuildingDetailsHeader(),
			isSticky: false,
			title: 'Building Details',
			titleFontSize: 'xsm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	}

	/**
	 * Returns building details comparative data row props. 
	 **/
	const applyBuildingDetailsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.constructionTypeInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.constructionTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.constructionTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.foundationType.foundationTypeSelectionListInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.foundationType.foundationTypeDetailSelectionListInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.foundationType.foundationTypeDetailSelectionListInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.buildingReplacementCostValueInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.buildingReplacementCostValueInput.getValueAsCurrencyFormat(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.buildingReplacementCostValueInput.getValueAsCurrencyFormat(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.designatedFloodZoneInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.designatedFloodZoneInput.value,
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.designatedFloodZoneInput.value,
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.buildingUseInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.buildingUseInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.buildingUseInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.parkingTypeInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.parkingTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormBuildingInfo.subformBuildingInfo.parkingTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.squareFootageInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.squareFootageInput.value?.toString(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.squareFootageInput.value?.toString(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.numberOfStoriesSelectionListInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.numberOfStoriesSelectionListInput.value?.toString(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.numberOfStoriesSelectionListInput.value?.toString(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldFormBuildingInfo.subformBuildingInfo.propertyInfo.yearOfConstructionInput.label,
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldFormBuildingInfo.subformBuildingInfo.propertyInfo.yearOfConstructionInput.value?.toString(),
				newValue: newFormBuildingInfo.subformBuildingInfo.propertyInfo.yearOfConstructionInput.value?.toString(),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			}
		];
	};

	// #endregion building details

	// #region building limits

	/**
	 * Returns building limits comparative data header props.
	 */
	const applyBuildingLimitsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForBuildingLimitsHeader(),
			isSticky: false,
			title: 'Limits',
			titleFontSize: 'xsm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	}


	/**
	 * Returns building limits comparative data row props. 
	 **/
	const applyBuildingLimitsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: oldBuildingCoverageLimitsInput.getBuildingLimitLabel(),
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldBuildingCoverageLimitsInput.getBuildingLimitAsCurrencyFormat(comparativeEntitySummary.entityId),
				newValue: newBuildingCoverageLimitsInput.getBuildingLimitAsCurrencyFormat(comparativeEntitySummary.entityId),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldBuildingCoverageLimitsInput.getContentsLimitLabel(),
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldBuildingCoverageLimitsInput.getContentsLimitAsCurrencyFormat(comparativeEntitySummary.entityId),
				newValue: newBuildingCoverageLimitsInput.getContentsLimitAsCurrencyFormat(comparativeEntitySummary.entityId),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
			{
				fieldLabel: oldBuildingCoverageLimitsInput.getBusinessIncomeExtraExpenseLimitLabel(),
				oldValue: comparativeEntitySummary.isEntityAdded ? 'N/A' : oldBuildingCoverageLimitsInput.getBusinessIncomeExtraExpenseLimitAsCurrencyFormat(comparativeEntitySummary.entityId),
				newValue: newBuildingCoverageLimitsInput.getBusinessIncomeExtraExpenseLimitAsCurrencyFormat(comparativeEntitySummary.entityId),
				hideNewValueIfSame: !comparativeEntitySummary.isEntityAdded,
				hideNewValueIfUndefinedOrEmpty: comparativeEntitySummary.isEntityAdded || comparativeEntitySummary.isEntityRemoved,
			},
		];
	};

	// #endregion building limits

	// #endregion exported methods


	// #region private methods

	/**
	 * Generates id for the address header.
	 */
	const generateIdForAddressHeader = (): string => {
		return `${comparativeEntitySummary.baseId}-address-header`;
	}

	/**
	 * Generates id for the building details header.
	 */
	const generateIdForBuildingDetailsHeader = (): string => {
		return `${comparativeEntitySummary.baseId}-building-details-header`;
	}

	/**
	 * Generates id for the building limits header.
	 */
	const generateIdForBuildingLimitsHeader = (): string => {
		return `${comparativeEntitySummary.baseId}-building-limits-header`;
	}

	/**
	 * Generates id for the building coverage limits input.
	 */
	const generateIdForBuildingCoverageLimitsInput = (instanceType: EntityInstanceType): string => {
		return `${comparativeEntitySummary.baseId}-${instanceType}-buildingCoverageLimits`;
	}

	/**
	 * Generates id for the building info form.
	 */
	const generateIdForFormBuildingInfo = (instanceType: EntityInstanceType): string => {
		return `${comparativeEntitySummary.baseId}-${instanceType}-formBuildingInfo`;
	}

	/**
	 *  Converts the building instance into a list of building limits view summaries.
	 */
	const getBuildingLimitsViewSummaries = (building: BuildingResponseCommercialExisting | undefined): BuildingLimitsViewSummary[] => {
		let buildingLimitsViewSummaries: BuildingLimitsViewSummary[] = [];
		if (building !== undefined) {

			let buildingLimitsViewSummary: BuildingLimitsViewSummary = new BuildingLimitsViewSummary();
			buildingLimitsViewSummary.buildingGuid = building.id;
			buildingLimitsViewSummary.buildingDescription = building.buildingData?.buildingDescription ?? null;
			buildingLimitsViewSummary.buildingReplacementCostValue = building.buildingData?.buildingReplacementCostValue ?? null;
			buildingLimitsViewSummary.buildingAddress = AddressService.getInstance().formatAddressToSingleLineString(building.address?.address ?? new Address());
			buildingLimitsViewSummary.building = building.limits?.building ?? null;
			buildingLimitsViewSummary.contents = building.limits?.contents ?? null;
			buildingLimitsViewSummary.businessIncomeExtraExpense = building.limits?.businessIncomeExtraExpense ?? null;

			buildingLimitsViewSummaries = [
				buildingLimitsViewSummary,
			];
		}
		return buildingLimitsViewSummaries;
	}
	// #endregion private methods


	// #region variables

	const oldPropertyAddress: AddressLocationWithAccuracy = React.useMemo(
		() => params.props.comparisonEntitySummary.oldData?.address ?? new AddressLocationWithAccuracy(),
		[params.props.comparisonEntitySummary.oldData],
	);
	const newPropertyAddress: AddressLocationWithAccuracy = React.useMemo(
		() => params.props.comparisonEntitySummary.newData?.address ?? new AddressLocationWithAccuracy(),
		[params.props.comparisonEntitySummary.newData],
	);
	const oldBuildingLimitsViewSummaries: BuildingLimitsViewSummary[] = React.useMemo(
		() => getBuildingLimitsViewSummaries(params.props.comparisonEntitySummary.oldData),
		[params.props.comparisonEntitySummary.oldData],
	);
	const newBuildingLimitsViewSummaries: BuildingLimitsViewSummary[] = React.useMemo(
		() => getBuildingLimitsViewSummaries(params.props.comparisonEntitySummary.newData),
		[params.props.comparisonEntitySummary.newData],
	);

	// Comparative entity summary.
	const comparativeEntitySummary: UseComparativeEntitySummaryResults = useComparativeEntitySummary({
		id: params.props.id,
		props: params.props,
	});

	// Old building coverage limits input.
	const oldBuildingCoverageLimitsInput: UseInputBuildingCoverageLimitsResults = useInputBuildingCoverageLimits({
		id: generateIdForBuildingCoverageLimitsInput(EntityInstanceType.OLD),
		disabled: false,
		required: true,
		buildingLimitsViewSummaries: oldBuildingLimitsViewSummaries,
	});

	// Old building info form.
	const oldFormBuildingInfo: UseFormBuildingInfoResults = useFormBuildingInfo({
		id: generateIdForFormBuildingInfo(EntityInstanceType.OLD),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			propertyAddress: oldPropertyAddress,
			propertyDomainValuesProps: params.props.propertyDomainValuesProps,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			hasFloodPeril: params.props.oldHasFloodPerilSelected,
			propertyInfo: params.props.comparisonEntitySummary.oldData?.buildingData ?? undefined,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			onRequestToChangeBuildingAddress: () => { },
			onRequestToChangeBuildingLocation: () => { },
			onSaveBuildingInfo: (buildingRiskDataCommercial: BuildingRiskDataCommercial) => { },
			showValidationsOnMount: true,
		},
	});

	// New building coverage limits input.
	const newBuildingCoverageLimitsInput: UseInputBuildingCoverageLimitsResults = useInputBuildingCoverageLimits({
		id: generateIdForBuildingCoverageLimitsInput(EntityInstanceType.NEW),
		disabled: false,
		required: true,
		buildingLimitsViewSummaries: newBuildingLimitsViewSummaries,
	});

	// New building info form.
	const newFormBuildingInfo: UseFormBuildingInfoResults = useFormBuildingInfo({
		id: generateIdForFormBuildingInfo(EntityInstanceType.NEW),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			propertyAddress: newPropertyAddress,
			propertyDomainValuesProps: params.props.propertyDomainValuesProps,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			hasFloodPeril: params.props.newHasFloodPerilSelected,
			propertyInfo: params.props.comparisonEntitySummary.newData?.buildingData ?? undefined,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			onRequestToChangeBuildingAddress: () => { },
			onRequestToChangeBuildingLocation: () => { },
			onSaveBuildingInfo: (buildingRiskDataCommercial: BuildingRiskDataCommercial) => { },
			showValidationsOnMount: true,
		},
	});

	// Checks to see if the address should be rendered if either the old or new mortgagee data is an address record.
	const showTheAddressComparison: boolean = React.useMemo(() => {
		return ((params.props.comparisonEntitySummary.oldData?.address ?? params.props.comparisonEntitySummary.newData?.address) ?? undefined) !== undefined;
	}, [params.props.comparisonEntitySummary.oldData?.address, params.props.comparisonEntitySummary.newData?.address]);

	const showBuildingDetails: boolean = React.useMemo(() => {
		return (params.props.comparisonEntitySummary.oldData?.buildingData !== undefined && params.props.comparisonEntitySummary.oldData?.buildingData !== null)
			|| (params.props.comparisonEntitySummary.newData?.buildingData !== undefined && params.props.comparisonEntitySummary.newData?.buildingData !== null);
	}, [params.props.comparisonEntitySummary.oldData?.buildingData, params.props.comparisonEntitySummary.newData?.buildingData]);

	const showBuildingLimits: boolean = React.useMemo(() => {
		return (params.props.comparisonEntitySummary.oldData?.limits !== undefined && params.props.comparisonEntitySummary.oldData?.limits !== null)
			|| (params.props.comparisonEntitySummary.newData?.limits !== undefined && params.props.comparisonEntitySummary.newData?.limits !== null);
	}, [params.props.comparisonEntitySummary.oldData?.limits, params.props.comparisonEntitySummary.newData?.limits]);
	// #endregion private variables

	return {
		showTheAddressComparison,
		showBuildingDetails,
		showBuildingLimits,
		comparativeEntitySummary,
		oldBuildingCoverageLimitsInput,
		oldFormBuildingInfo,
		newBuildingCoverageLimitsInput,
		newFormBuildingInfo,
		applyAddressComparativeDataHeaderProps,
		applyBuildingDetailsComparativeDataHeaderProps,
		applyBuildingLimitsComparativeDataHeaderProps,
		applyAddressComparativeDataRowProps,
		applyBuildingDetailsComparativeDataRowProps,
		applyBuildingLimitsComparativeDataRowProps, 
	};
}

// Export parameters.
export {
	UseComparativeBuildingSummaryParameters,
}

// Export results.
export {
	UseComparativeBuildingSummaryResults,
}

export default useComparativeBuildingSummary;